import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import slugify from 'slugify';
import { datetimeToText } from '../Forum';
import { update as updateUser } from 'redux/modules/users';
import { read as readQuestion } from 'redux/modules/questions';
import { card } from '../Forum.module.scss';
import styles from './Postcard.module.scss';

const Stats = ({ nVotes, views, nAnswers, nComments, question }) => {
  return (
    <div className={styles.mainStats}>
      <div className={styles.stats}>
        <div>
          <div>{nVotes}</div>
          <div>votes</div>
        </div>
        <div>
          <div>{question ? nAnswers : nComments}</div>
          <div>{question ? 'answers' : 'comments'}</div>
        </div>
        {/* <div>
          <div>{views}</div>
          <div>views</div>
        </div> */}
      </div>
      {!question && <div className={styles.answer}>Accepted Answer</div>}
    </div>
  );
};

Stats.propTypes = {
  question: PropTypes.bool.isRequired,
  nVotes: PropTypes.number.isRequired,
  views: PropTypes.number.isRequired,
  nAnswers: PropTypes.number,
  nComments: PropTypes.number,
};

Stats.defaultProps = {
  question: true,
  nVotes: 0,
  views: 0,
  nAnswers: 0,
  nComments: 0,
};

const Answer = ({ answer }) => {
  const [displayMore, setDisplayMore] = useState(false);
  const [hasMore, sethasMore] = useState(false);

  const answerRef = useRef();
  const readMore = _ => setDisplayMore(!displayMore);

  useEffect(_ => {
    const e = answerRef.current;
    const hasOverflowingChildren = e.offsetHeight < e.scrollHeight;
    sethasMore(hasOverflowingChildren);
  }, []);

  return (
    <div>
      <div
        className={cx(
          styles.answer,
          !displayMore && hasMore && styles.more,
          displayMore && styles.displayMore
        )}
        ref={answerRef}
      >
        <ReactMarkdown source={answer} />
      </div>
      {hasMore && (
        <div className={styles.readmore} onClick={readMore}>
          Read {displayMore ? 'Less' : 'More'}
        </div>
      )}
    </div>
  );
};

Answer.propTypes = { answer: PropTypes.string.isRequired };

const Postcard = ({
  _id,
  question,
  views,
  nVotes,
  tags,
  author,
  subscribed,
  nAnswers,
  answer,
  createdAt,
  users,
  isAuth,
  user,
  updateUser,
  readQuestion,
  topic,
}) => {
  const disabled = !isAuth || topic === 'activities';
  const handleSubscribe = useCallback(
    subscribed => () => {
      if (disabled) return;
      if (subscribed) {
        return updateUser({
          _id: user._id,
          subscribedQuestions: (user.subscribedQuestions || []).filter(
            question => question !== _id
          ),
        }).then(() => readQuestion({ id: _id }));
      } else {
        return updateUser({
          _id: user._id,
          subscribedQuestions: [
            ...new Set([...(user.subscribedQuestions || []), _id]),
          ],
        }).then(() => readQuestion({ id: _id }));
      }
    },
    [updateUser, readQuestion, user, _id, disabled]
  );
  const questioner = users[author];
  const asked = datetimeToText(createdAt);
  const answerer = answer && users[answer.author];
  const answered = answer && datetimeToText(answer.createdAt);
  return (
    <div className={cx(card, styles.mainContainer)}>
      <div className={styles.mainCard}>
        <Stats {...{ nVotes, views, nAnswers }} question={true} />
        <div className={styles.details}>
          <div className={styles.title}>
            <Link
              to={`/forum/questions/${_id}/${slugify(question)}/`}
            >
              {question}
            </Link>
          </div>
          <div className={styles.description}>
            <div className={styles.tagContainer}>
              {tags.map((tag, i) => (
                <div key={i}>{tag}</div>
              ))}
            </div>
            <div className={styles.information}>
              <FontAwesomeIcon
                icon="rss"
                className={cx(
                  subscribed && styles.highlight,
                  disabled && styles.disable
                )}
                onClick={handleSubscribe(subscribed)}
              />
              <span>
                asked {asked} ago by{' '}
                <Link to={`/users/${author}/profile/`}>
                  {questioner && questioner.name}
                </Link>
              </span>
            </div>
          </div>
        </div>
        {/* <FontAwesomeIcon icon="ellipsis-v" className={styles.menu} /> */}
      </div>
      {answer && (
        <div className={styles.seperator}>
          <hr />
        </div>
      )}
      {answer && (
        <div className={styles.mainCard}>
          <Stats {...answer} question={false} />
          <div className={styles.details}>
            <Answer answer={answer.answer} />
            <div className={styles.description}>
              <div className={styles.information}>
                <span>
                  answered {answered} ago by{' '}
                  <Link to={`/users/${answer.author}/profile/`}>
                    {answerer && answerer.name}
                  </Link>
                </span>
              </div>
            </div>
          </div>
          {/* <FontAwesomeIcon icon="ellipsis-v" className={styles.menu} /> */}
        </div>
      )}
    </div>
  );
};

Postcard.propTypes = {
  _id: PropTypes.string.isRequired,
  question: PropTypes.string.isRequired,
  // views: PropTypes.number.isRequired,
  nVotes: PropTypes.number.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string.isRequired),
  author: PropTypes.string.isRequired,
  subscribed: PropTypes.bool.isRequired,
  nAnswers: PropTypes.number.isRequired,
  answer: PropTypes.shape({
    answer: PropTypes.string.isRequired,
    author: PropTypes.string.isRequired,
    nVotes: PropTypes.number.isRequired,
    nComments: PropTypes.number.isRequired,
    // views: PropTypes.number.isRequired,
    createdAt: PropTypes.string.isRequired,
  }),
  createdAt: PropTypes.string.isRequired,
  users: PropTypes.object.isRequired,
  updateUser: PropTypes.func.isRequired,
  readQuestion: PropTypes.func.isRequired,
  topic: PropTypes.string.isRequired,
};

Postcard.defaultProps = {
  nVotes: 0,
  nAnswers: 0,
  views: 0,
  subscribed: false,
};

export default connect(
  ({ entities: { users, answers }, auth: { user } }, { acceptedAnswer }) => ({
    users,
    isAuth: !!user,
    user: user && users[user],
    answer: answers[acceptedAnswer],
  }),
  { updateUser, readQuestion }
)(Postcard);
